import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

function CrumbSegment({ crumb, idx, isLast, onCrumb }) {
  const className = [
    'sb-drive-crumb',
    isLast ? 'sb-drive-crumb--current' : '',
    idx === 0 ? 'sb-drive-crumb--first' : '',
    isLast ? 'sb-drive-crumb--last' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (isLast) {
    return (
      <span className={className} title={crumb.name}>
        {crumb.name}
      </span>
    );
  }

  return (
    <button type="button" className={className} onClick={() => onCrumb?.(crumb.id)} title={crumb.name}>
      {crumb.name}
    </button>
  );
}

function FullTrail({ trail, onCrumb }) {
  const lastIdx = trail.length - 1;
  return trail.map((crumb, idx) => (
    <React.Fragment key={crumb.id ?? `root-${idx}`}>
      {idx > 0 ? <span className="sb-drive-crumb-sep" aria-hidden>›</span> : null}
      <CrumbSegment crumb={crumb} idx={idx} isLast={idx === lastIdx} onCrumb={onCrumb} />
    </React.Fragment>
  ));
}

function CollapsedTrail({ trail, onCrumb }) {
  const lastIdx = trail.length - 1;
  const middleTitle = trail
    .slice(1, -1)
    .map((c) => c.name)
    .join(' / ');

  return (
    <>
      <CrumbSegment crumb={trail[0]} idx={0} isLast={false} onCrumb={onCrumb} />
      <span className="sb-drive-crumb-sep" aria-hidden>›</span>
      <span className="sb-drive-crumb-ellipsis" title={middleTitle} aria-label={middleTitle}>…</span>
      <span className="sb-drive-crumb-sep" aria-hidden>›</span>
      <CrumbSegment crumb={trail[lastIdx]} idx={lastIdx} isLast onCrumb={onCrumb} />
    </>
  );
}

export default function StorageBoxBreadcrumbTrail({ trail, onCrumb }) {
  const wrapRef = useRef(null);
  const measureRef = useRef(null);
  const [collapseMiddle, setCollapseMiddle] = useState(false);
  const [squeezeSegments, setSqueezeSegments] = useState(false);

  const updateCollapse = useCallback(() => {
    const wrap = wrapRef.current;
    const measure = measureRef.current;
    if (!wrap || !measure) {
      setCollapseMiddle(false);
      setSqueezeSegments(false);
      return;
    }
    const available = wrap.clientWidth;
    const needed = measure.scrollWidth;
    const overflows = needed > available + 1;
    if (trail.length <= 2) {
      setCollapseMiddle(false);
      setSqueezeSegments(overflows);
      return;
    }
    setCollapseMiddle(overflows);
    setSqueezeSegments(false);
  }, [trail]);

  useLayoutEffect(() => {
    updateCollapse();
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const ro = new ResizeObserver(() => updateCollapse());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [updateCollapse, trail]);

  if (!trail.length) return null;

  const trailClass = [
    'sb-drive-crumb-trail',
    collapseMiddle && trail.length > 2 ? 'sb-drive-crumb-trail--collapsed' : '',
    squeezeSegments ? 'sb-drive-crumb-trail--squeeze' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="sb-drive-crumb-trail-wrap" ref={wrapRef}>
      <nav className={trailClass} aria-label="Current folder">
        {collapseMiddle && trail.length > 2 ? (
          <CollapsedTrail trail={trail} onCrumb={onCrumb} />
        ) : (
          <FullTrail trail={trail} onCrumb={onCrumb} />
        )}
      </nav>
      <div ref={measureRef} className="sb-drive-crumb-trail sb-drive-crumb-trail--measure" aria-hidden>
        <FullTrail trail={trail} onCrumb={onCrumb} />
      </div>
    </div>
  );
}
